import React from "react";
import { useSocial } from "../../contexts/SocialContext";

/**
 * Small coloured dot indicating hardware device status.
 * Matches the firmware overlay colour table:
 *   grey   = connected, idle
 *   orange = loaded / ready
 *   green  = running
 *
 * Hidden entirely unless a hardware device is actually connected this session
 * (presence online) or driving a live state. A device that is merely linked to
 * the account but dormant shows nothing — the dot is contextual, not ambient.
 */
export default function DeviceConnectionDot() {
  const social = useSocial();
  const presence = social?.devicePresence;
  const devState = social?.liveDeviceState;

  const state = devState?.state;
  const isLiveActive =
    !!state &&
    state !== "Inactive" &&
    state !== "Clear" &&
    state !== "Complete";

  // Only treat the device as connected when presence has positively reported
  // online. A null/absent presence (no device ever connected) is NOT online —
  // the backend only emits device:presence for devices that are actually live.
  const isConnected = presence?.online === true;

  // Issue #6: remove the dot entirely when not connected to a device.
  if (!isConnected && !isLiveActive) return null;

  let colour = "bg-zinc-500"; // grey — connected, idle
  let label = "Device idle";

  if (state === "Ready") {
    colour = "bg-orange-500";
    label = `Device ready${devState?.title ? `: ${devState.title}` : ""}`;
  } else if (isLiveActive) {
    colour = "bg-green-500";
    const phase = devState?.phase_label || state;
    label = `Device running: ${phase}`;
    if (devState?.remaining_s != null) {
      const m = Math.floor(devState.remaining_s / 60);
      const s = devState.remaining_s % 60;
      label += ` ${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
  }

  const pulse = state === "Ready" || isLiveActive;

  return (
    <span className="relative flex h-3 w-3 cursor-default" title={label}>
      {/* Pulse ring for active states */}
      {pulse && (
        <span
          className={`absolute inset-0 rounded-full ${colour} opacity-40 animate-ping`}
        />
      )}
      <span className={`relative inline-flex h-3 w-3 rounded-full ${colour}`} />
    </span>
  );
}
